import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { Modal, Button, Form, Spinner, Badge, Alert } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import { NetworkAccountService } from "src/shared/services/contentNetwork/networkAccounts/networkAccountService";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { NetworkResource } from "@models/networkResource";

interface Props {
  show: boolean;
  onClose: () => void;
  network: string;
  currentResourceId: number | null;
  currentSubResourceId: number | null;
  onResourceSelected: (resourceId: number, subResourceId?: number) => void;
}

// Define which resource type each network uses
// type: the resource type we list/select first
// needsParent: if true, selected resource has a parent ACCOUNT resource that must also be chosen
const NETWORK_RESOURCE_TYPES: Record<string, { type: string; label: string; needsParent: boolean }> = {
  // Google: choose a YouTube channel that is linked to an Account (parent),
  // mirroring the Pinterest account+board flow.
  google: { type: "youtube_channel", label: "Channel", needsParent: true },
  tiktok: { type: "account", label: "Account", needsParent: false },
  pinterest: { type: "board", label: "Board", needsParent: true },
  meta: { type: "page", label: "Page", needsParent: true },
  snapchat: { type: "profile", label: "Profile", needsParent: true },
  linkedin: { type: "page", label: "Page", needsParent: true },
  // Vibe has no sub-resource: campaigns, creatives and audiences all hang off the
  // advertiser, which is the account resource itself. It is listed rather than left out
  // so the copy says "Advertiser", which is the word Vibe uses.
  vibe: { type: "account", label: "Advertiser", needsParent: false },
};

// What an unmapped network gets. The comment here used to claim a missing entry "falls
// through to the account-only flow", but nothing implemented that: resourceConfig came
// back undefined and the first `resourceConfig.type` threw, so the modal rendered as a
// blank page. Vibe was the first network to have no entry and so the first to hit it.
const ACCOUNT_ONLY = { type: "account", label: "Account", needsParent: false };

const NETWORK_DISPLAY_NAMES: Record<string, string> = {
  meta: "Meta",
  pinterest: "Pinterest",
  snapchat: "Snapchat",
  tiktok: "TikTok",
  google: "Google Ads",
  linkedin: "LinkedIn",
  vibe: "Vibe",
};

export const NetworkResourceSelectorModal = ({
  show,
  onClose,
  network,
  currentResourceId,
  currentSubResourceId,
  onResourceSelected,
}: Props) => {
  const networkAccountSvc = new NetworkAccountService();
  const { selectedEngagement, selectedLaunch } = useSnapshot(engagementStore);

  const [step, setStep] = useState(1);
  const [resources, setResources] = useState<NetworkResource[]>([]);
  const [parentAccounts, setParentAccounts] = useState<NetworkResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<NetworkResource | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingParents, setLoadingParents] = useState(false);
  const [saving, setSaving] = useState(false);

  const networkKey = network.toLowerCase();
  const networkDisplayName = NETWORK_DISPLAY_NAMES[networkKey] || network;
  const resourceConfig = NETWORK_RESOURCE_TYPES[networkKey] ?? ACCOUNT_ONLY;

  useEffect(() => {
    if (show) {
      loadResources();
    }
  }, [show, network]);

  const loadResources = async () => {
    setLoading(true);
    try {
      const clientId = selectedEngagement?.client?.id;

      if (!clientId || clientId === -1) {
        setResources([]);
        setLoading(false);
        return;
      }

      const launchOrgId = selectedLaunch?.organizations?.[0]?.id;
      const organizationIds =
        launchOrgId && launchOrgId > 0 && launchOrgId !== clientId
          ? [clientId, launchOrgId]
          : [clientId];

      const allResources = await networkAccountSvc.listNetworkResources({
        organization_ids: organizationIds,
        networks: [network],
        types: [resourceConfig.type],
      });

      setResources(allResources);
    } catch (err) {
      console.error("Failed to load network resources:", err);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const loadParentAccounts = async (resource: NetworkResource) => {
    setLoadingParents(true);
    try {
      if (!resource.parent_ids || resource.parent_ids.length === 0) {
        setParentAccounts([]);
        setLoadingParents(false);
        return;
      }

      const parents = await networkAccountSvc.listNetworkResources({
        resource_ids: resource.parent_ids,
      });

      setParentAccounts(parents);
    } catch (err) {
      console.error("Failed to load parent accounts:", err);
      setParentAccounts([]);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleResourceSelect = async (resource: NetworkResource) => {
    setSelectedResource(resource);

    if (resourceConfig.needsParent) {
      // Need to select parent account
      setStep(2);
      await loadParentAccounts(resource);
    } else {
      // Can confirm immediately (account types)
      setSelectedParentId(resource.id);
    }
  };

  const handleBack = () => {
    setStep(1);
    setSelectedResource(null);
    setSelectedParentId(null);
    setParentAccounts([]);
  };

  const handleConfirm = () => {
    if (resourceConfig.needsParent) {
      // For boards/pages/profiles, need both parent and sub-resource
      if (!selectedParentId || !selectedResource) return;
      
      setSaving(true);
      setTimeout(() => {
        onResourceSelected(selectedParentId, selectedResource.id);
        setSaving(false);
        handleClose();
      }, 300);
    } else {
      // For accounts, just need the resource
      if (!selectedResource) return;
      
      setSaving(true);
      setTimeout(() => {
        onResourceSelected(selectedResource.id);
        setSaving(false);
        handleClose();
      }, 300);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedResource(null);
    setSelectedParentId(null);
    setParentAccounts([]);
    onClose();
  };

  const handleSetupNew = () => {
    window.open("/settings/network-accounts", "_blank");
  };

  const renderResourceCard = (resource: NetworkResource, isCurrent: boolean = false) => {
    const isSelected = selectedResource?.id === resource.id;

    return (
      <div
        key={resource.id}
        className={`border rounded-3 p-3 mb-2 cursor-pointer transition-all ${
          isSelected
            ? "border-primary border-2 bg-light"
            : "border-secondary"
        }`}
        style={{
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onClick={() => handleResourceSelect(resource)}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <Form.Check
              type="radio"
              checked={isSelected}
              onChange={() => handleResourceSelect(resource)}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <strong className="mb-0">{resource.name}</strong>
                {isCurrent && (
                  <Badge bg="success" className="px-2">
                    Current
                  </Badge>
                )}
                <Badge bg="light" text="dark" className="px-2">
                  {resourceConfig.label}
                </Badge>
              </div>
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  <i className="bi bi-hash me-1"></i>
                  {resource.external_id || "No ID"}
                </small>
              </div>
            </div>
          </div>

          {isSelected && (
            <i className="bi bi-check-circle-fill text-primary fs-4"></i>
          )}
        </div>
      </div>
    );
  };

  const renderParentAccountCard = (parent: NetworkResource) => {
    const isSelected = selectedParentId === parent.id;
    const isCurrent = currentResourceId === parent.id;

    return (
      <div
        key={parent.id}
        className={`border rounded-3 p-3 mb-2 cursor-pointer transition-all ${
          isSelected
            ? "border-primary border-2 bg-light"
            : "border-secondary"
        }`}
        style={{
          cursor: "pointer",
          transition: "all 0.2s ease",
        }}
        onClick={() => setSelectedParentId(parent.id)}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <Form.Check
              type="radio"
              checked={isSelected}
              onChange={() => setSelectedParentId(parent.id)}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <strong className="mb-0">{parent.name}</strong>
                {isCurrent && (
                  <Badge bg="success" className="px-2">
                    Current
                  </Badge>
                )}
                <Badge bg="light" text="dark" className="px-2">
                  Account
                </Badge>
              </div>
              <div className="d-flex align-items-center gap-3">
                <small className="text-muted">
                  <i className="bi bi-hash me-1"></i>
                  {parent.external_id || "No ID"}
                </small>
              </div>
            </div>
          </div>

          {isSelected && (
            <i className="bi bi-check-circle-fill text-primary fs-4"></i>
          )}
        </div>
      </div>
    );
  };

  const renderStep1 = () => {
    if (loading) {
      return <Loader />;
    }

    if (resources.length === 0) {
      return (
        <Alert variant="info" className="mb-0">
          <div className="d-flex align-items-start gap-3">
            <i className="bi bi-info-circle fs-4"></i>
            <div className="flex-grow-1">
              <strong>No {networkDisplayName} {resourceConfig.label.toLowerCase()}s found</strong>
              <p className="mb-0 mt-1">
                You need to set up {resourceConfig.label.toLowerCase()}s for the client organization 
                before you can use this network. Click "Set Up New Resource" below to get started.
              </p>
            </div>
          </div>
        </Alert>
      );
    }

    return (
      <>
        <p className="text-muted mb-3">
          Select which {networkDisplayName} {resourceConfig.label.toLowerCase()} to use for this launch.
        </p>

        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {resources.map((resource) => {
            const isCurrent = resourceConfig.needsParent 
              ? currentSubResourceId === resource.id
              : currentResourceId === resource.id;
            return renderResourceCard(resource, isCurrent);
          })}
        </div>
      </>
    );
  };

  const renderStep2 = () => {
    if (loadingParents) {
      return <Loader />;
    }

    if (parentAccounts.length === 0) {
      return (
        <Alert variant="warning" className="mb-0">
          <div className="d-flex align-items-start gap-3">
            <i className="bi bi-exclamation-triangle fs-4"></i>
            <div className="flex-grow-1">
              <strong>No linked accounts found</strong>
              <p className="mb-0 mt-1">
                This {resourceConfig.label.toLowerCase()} is not linked to any accounts. 
                Please link it to an account in the Network Resources settings first.
              </p>
            </div>
          </div>
        </Alert>
      );
    }

    return (
      <>
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-info-circle text-primary"></i>
            <div>
              <strong>Selected {resourceConfig.label}:</strong> {selectedResource?.name}
            </div>
          </div>
        </div>

        <p className="text-muted mb-3">
          Select which account to use with this {resourceConfig.label.toLowerCase()}.
        </p>

        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {parentAccounts.map(renderParentAccountCard)}
        </div>
      </>
    );
  };

  const canConfirm = () => {
    if (resourceConfig.needsParent) {
      return step === 2 && selectedParentId !== null && selectedResource !== null;
    } else {
      return selectedResource !== null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      className="tier-2-modal"
      backdropClassName="tier-2-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 1 
            ? `Select ${networkDisplayName} ${resourceConfig.label}`
            : `Select Account for ${resourceConfig.label}`
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {step === 1 ? renderStep1() : renderStep2()}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {step === 2 && (
            <Button
              variant="outline-secondary"
              onClick={handleBack}
              className="d-flex align-items-center gap-2"
            >
              <i className="bi bi-arrow-left"></i>
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              variant="outline-primary"
              onClick={handleSetupNew}
              className="d-flex align-items-center gap-2"
            >
              <i className="bi bi-plus-circle"></i>
              Set Up New Resource
            </Button>
          )}
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          {step === 1 && !resourceConfig.needsParent && (
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canConfirm() || saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : (
                "Confirm Selection"
              )}
            </Button>
          )}
          {step === 2 && (
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canConfirm() || saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : (
                "Confirm Selection"
              )}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};